/**
 * ConfirmDialog - A reusable confirmation dialog/modal for React Native Paper
 *
 * @example
 * <ConfirmDialog
 *   visible={confirmDialog}
 *   onDismiss={() => setConfirmDialog(false)}
 *   title='Delete Work Order'
 *   content='Are you sure you want to delete this work order?'
 *   confirmLabel='Delete'
 *   cancelLabel='Cancel'
 *   onConfirm={handleDelete}
 *   confirmLoading={isSubmitting}
 *   danger
 * />
 */
import React from 'react';
import { useWindowDimensions } from 'react-native';

import { Button, Dialog, Portal, Text } from '@/components/ui';
import type { ButtonProps } from '@/components/ui';
import { alertStyles } from '@/styles/alert';
import { buttonStyles } from '@/styles/components/buttons';
import { colors } from '@/styles/theme';

interface ConfirmDialogProps {
	visible: boolean;
	onDismiss: () => void;
	title: React.ReactNode;
	content: React.ReactNode;
	onConfirm: () => void;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmLoading?: boolean;
	confirmButtonProps?: Partial<ButtonProps>;
	cancelButtonProps?: Partial<ButtonProps>;
	danger?: boolean;
	style?: any;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
	visible,
	onDismiss,
	title,
	content,
	onConfirm,
	style,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	confirmLoading = false,
	confirmButtonProps = {},
	cancelButtonProps = {},
	danger = false,
}) => {
	const { width } = useWindowDimensions();
	const isMobile = width < 768;

	// Just re-use Alert
	return (
		<Portal>
			<Dialog
				visible={visible}
				onDismiss={onDismiss}
				style={[
					isMobile ? alertStyles.mobileDialog : alertStyles.desktopDialog,
					style,
				]}
				testID="confirm-dialog"
			>
				<Dialog.Title>{title}</Dialog.Title>
				<Dialog.Content>
					{typeof content === 'string' ? <Text>{content}</Text> : content}
				</Dialog.Content>
				<Dialog.Actions>
					<Button
						style={alertStyles.button}
						mode='outlined'
						onPress={onDismiss}
						accessibilityLabel={cancelLabel}
						testID="btn-cancel-dialog"
						{...cancelButtonProps}
					>
						<Text>{cancelLabel}</Text>
					</Button>
					<Button
						mode='contained'
						onPress={onConfirm}
						loading={confirmLoading}
						buttonColor={danger ? colors.error : undefined}
						textColor={danger ? colors.textLight : undefined}
						accessibilityLabel={confirmLabel}
						testID="btn-confirm-dialog"
						style={{
							...alertStyles.button,
							...buttonStyles.button,
							...(danger && buttonStyles.danger),
						}}
						{...confirmButtonProps}
					>
						<Text style={danger ? buttonStyles.dangerText : undefined}>{confirmLabel}</Text>
					</Button>
				</Dialog.Actions>
			</Dialog>
		</Portal>
	);
};
